import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { Tooltip } from "@altimateai/lego";
import {
  FilterType,
  TableHeaderType,
  type APIFilterParams,
  createAPIDropdownFilter,
  SortDirection,
} from "@/components/tables/types";
import { formatCost } from "@/helpers/utils";
import { getAIServiceFilterValues } from "@/helpers/apis";
import type {
  AIServiceFilterValuesResponse,
  AIServiceUsageResponse,
  ColumnDefinition,
} from "./types";

interface UseUsageTableHeadersParams {
  usageData: AIServiceUsageResponse | undefined;
  serviceType: string | undefined;
  dateParams: { start_date: string; end_date: string };
  sortAttribute: string;
  sortOrder: SortDirection;
  currency: string;
  currencySymbol: string;
  costFilterMode: "gt" | "lt" | "bt";
}

export const useUsageTableHeaders = ({
  usageData,
  serviceType,
  dateParams,
  sortAttribute,
  sortOrder,
  currency,
  currencySymbol,
  costFilterMode,
}: UseUsageTableHeadersParams): TableHeaderType<Record<string, unknown>>[] => {
  const createFilterApiFunction = useCallback(
    (columnKey: string) =>
      async ({ prefix, page, size }: APIFilterParams) => {
        if (!serviceType) return undefined;
        return getAIServiceFilterValues(serviceType, {
          ...dateParams,
          column: columnKey,
          prefix,
          page,
          size,
        });
      },
    [serviceType, dateParams]
  );

  const extractorFunction = useCallback((data: AIServiceFilterValuesResponse | undefined) => {
    return (
      data?.values?.map((val: string) => ({
        label: val,
        value: val,
      })) || []
    );
  }, []);

  return useMemo(() => {
    if (!usageData?.columns) return [];

    return usageData.columns.map((col: ColumnDefinition, index: number) => {
      let filter: TableHeaderType<Record<string, unknown>>["filter"];

      if (col.filterable && (col.data_type === "string" || col.data_type === "link")) {
        filter = createAPIDropdownFilter({
          filterType: FilterType.API_DROPDOWN,
          searchKey: col.key,
          apiFunction: createFilterApiFunction(col.key),
          extractorFunction,
          pageSize: 50,
          seeMore: true,
        });
      } else if (col.data_type === "number" && col.key.includes("cost")) {
        filter = {
          filterType: FilterType.TEXT,
          searchKey: "costFilter",
          label: col.label,
          placeHolder: `Specify cost in ${currencySymbol}`,
          filterMode: costFilterMode,
          filterModeSearchKey: "costFilterMode",
        };
      }

      const getTooltipInfo = () => {
        if (col.data_type === "datetime") return "All timestamps are in UTC";
        if (col.key === "model_name")
          return "Model name. Shown as 'unspecified' for Cortex LLM functions where a model is not specified as an argument.";
        return undefined;
      };

      return {
        id: index + 1,
        label: col.label,
        uid: col.key,
        tooltipInfo: getTooltipInfo(),
        sort: col.sortable
          ? {
              sortSearchKey: "sortAttribute",

              sortParamValue: col.key,
              value: sortAttribute === col.key ? sortOrder : "",
            }
          : undefined,
        filter,
        render: (item: Record<string, unknown>) => {
          const value = item[col.key];

          if (col.data_type === "link" && col.link_type && value) {
            let linkPath: string;
            if (col.link_type === "query") {
              linkPath = `/query/${value}`;
            } else {
              const instanceId = item.instance_id;
              const warehouseRk = item.warehouse_rk;
              linkPath =
                instanceId && warehouseRk
                  ? `/warehouse/${instanceId}/${warehouseRk}`
                  : `/warehouse`;
            }
            return (
              <Link
                to={linkPath}
                className="al-text-primary hover:al-underline"
                onClick={e => e.stopPropagation()}
              >
                {String(value)}
              </Link>
            );
          }

          if (col.data_type === "number" && col.key.includes("cost")) {
            return (
              <Tooltip title={String(Number(value))}>
                <span>{formatCost(Number(value), currency)}</span>
              </Tooltip>
            );
          }

          if (col.data_type === "number" && value != null) {
            return (
              <Tooltip title={String(value)}>
                <span>{String(value)}</span>
              </Tooltip>
            );
          }

          if (col.data_type === "datetime" && value) {
            return <span>{dayjs.utc(String(value)).format("MMM DD, YYYY HH:mm")}</span>;
          }

          if (col.key === "model_name" && (!value || String(value).trim() === "")) {
            return <span className="al-text-gray-400 al-italic">unspecified</span>;
          }

          return <span>{value != null ? String(value) : ""}</span>;
        },
      };
    });
  }, [
    usageData?.columns,
    sortAttribute,
    sortOrder,
    currency,
    currencySymbol,
    costFilterMode,
    createFilterApiFunction,
    extractorFunction,
  ]);
};
